import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isAwaitingShippingFee, isVirtualOnlyOrder } from '@mlm/shared';
import { getMe, getTenantConfig } from '../lib/api';
import { mySlotsQuery, unpaidOrdersQuery } from '../lib/lock-inputs';
import { BootSplash } from './boot-splash';

/**
 * Sends a member with a payable unpaid order to the payment page, from anywhere
 * in the portal, until they pay.
 *
 * Three conditions, ALL required:
 *
 *  1. the tenant turned `unpaid_order_lock_enabled` on (default off);
 *  2. the member registered through a CAMPAIGN / replicated page. Someone who
 *     arrived through a campaign came for one specific purchase, and abandoning
 *     it mid-funnel is the thing being solved. Someone who joined through an
 *     ordinary referral link is a member first and a buyer second — locking them
 *     out of their genealogy over an unpaid t-shirt would be indefensible;
 *  3. they actually have a PAYABLE unpaid order.
 *
 * "Payable" excludes orders awaiting an admin-set manual shipping fee. Those sit
 * at `pending` with no pay button at all, so locking someone onto one would be
 * inescapable — the same exclusion `UnpaidOrdersGate` already makes, for the
 * same reason.
 *
 * `UnpaidOrdersGate` stays mounted in the shell as the SOFT path for everyone
 * this doesn't cover. It can never double-fire: a locked member never reaches
 * the layout that renders it.
 *
 * Placed outside `<Layout />` so its destination can render without the app
 * shell, and its destination is declared outside this guard so it doesn't
 * redirect to itself forever.
 *
 * ── Why this HOLDS instead of passing through ──
 *
 * `<Outlet />` here is not a cheap placeholder: this guard wraps `<Layout />`,
 * so rendering it paints the entire app shell plus the destination page, with
 * all of that page's own queries. Returning it while still deciding is what
 * made a locked member watch a fully-hydrated dashboard for a beat before
 * being yanked to /pay. So an undecided render returns `<BootSplash />` — the
 * same markup the boot gate is already showing on a hard reload, which makes
 * the hold a seamless continuation of that splash rather than a new screen.
 *
 * "Never redirect on incomplete information" still holds, and is now stronger:
 * we neither redirect NOR admit anyone until the answer is known. Every
 * unknown-answer path fails OPEN (`<Outlet />`) rather than holding forever —
 * a failed /me or /orders must not strand a member on a splash.
 */
/** What the guard should do. `hold` = show a splash, decision not yet known. */
export type OrderGateDecision =
  | { kind: 'pass' }
  | { kind: 'hold' }
  | { kind: 'redirect'; orderId: string };

/**
 * The whole decision, as a pure function, because the ORDER of these checks is
 * the entire correctness argument and getting it wrong is not a cosmetic bug:
 *
 *  - `!lockEnabled` MUST be decided before any `*Pending` check. React Query
 *    reports `isPending: true` for a DISABLED query, so consulting a pending
 *    flag first would hold every member of every tenant on a splash FOREVER —
 *    the feature is opt-in and disabled everywhere today, so that is a total
 *    lockout of the member app, not a degraded corner case.
 *  - Every unknown answer fails OPEN. A failed /me, /slots or /orders returns
 *    `pass`, never `hold`; otherwise one network blip turns a cosmetic flash
 *    into a member who cannot reach the app at all.
 *  - `/pay/` short-circuits first so the destination is never gated on the
 *    guard's own queries and `redirect` can't loop.
 *
 * WHO IS HELD — all four, plus a payable order:
 *   1. the tenant turned the lock on (off by default, off on every live box);
 *   2. they registered through a CAMPAIGN page, not a plain referral/QR link;
 *   3. they have NO ACTIVE SLOT — i.e. they are still newly registered;
 *   4. the unpaid order is ALL-VIRTUAL.
 *
 * (3) and (4) keep this to the funnel it was built for: buy → pay → slot active
 * in one sitting, with nothing to ship. A physical kit is packed and posted
 * regardless, so trapping its buyer on a payment page buys nothing. (3) also
 * makes the lock self-releasing — the moment the kit is redeemed, or the slot
 * auto-creates under `kit_auto_create_slot.virtual`, they are free for good.
 */
export function decideOrderGate(input: {
  pathname: string;
  lockEnabled: boolean;
  mePending: boolean;
  registeredViaCampaign: boolean | undefined;
  slotsPending: boolean;
  hasAnyActiveSlot: boolean;
  ordersPending: boolean;
  orders:
    | Array<
        Parameters<typeof isAwaitingShippingFee>[0] &
          Parameters<typeof isVirtualOnlyOrder>[0] & { id: string }
      >
    | undefined;
}): OrderGateDecision {
  if (input.pathname.startsWith('/pay/')) return { kind: 'pass' };
  if (!input.lockEnabled) return { kind: 'pass' };
  if (input.mePending || input.slotsPending) return { kind: 'hold' };
  // Not a campaign signup — released without ever waiting on the order list.
  if (input.registeredViaCampaign !== true) return { kind: 'pass' };
  // Already activated. Not "newly registered", so never held again.
  if (input.hasAnyActiveSlot) return { kind: 'pass' };
  if (input.ordersPending) return { kind: 'hold' };
  if (!input.orders) return { kind: 'pass' };
  // Only an ALL-VIRTUAL order holds: nothing ships, so payment is the only thing
  // standing between them and having what they bought. An order awaiting an
  // admin-set shipping fee has no pay button at all, so holding on one would be
  // inescapable — and by construction it is never virtual-only anyway.
  const target = input.orders.find((o) => isVirtualOnlyOrder(o) && !isAwaitingShippingFee(o));
  return target ? { kind: 'redirect', orderId: target.id } : { kind: 'pass' };
}

export function RequireOrderSettled() {
  const location = useLocation();

  // Warm on first render: `useBootGate` awaits this exact key before any route
  // mounts, so `lockEnabled` is known synchronously and a lock-OFF tenant
  // (every live tenant today — the feature is opt-in) takes the first return
  // below having issued no queries and shown no splash. A failed config fetch
  // settles with no data and reads as OFF, which fails open by design.
  const tenantQ = useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig });
  const lockEnabled = tenantQ.data?.unpaidOrderLockEnabled === true;

  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe, enabled: lockEnabled });

  // All three keyed on `lockEnabled` alone, NOT on each other's answers: gating
  // orders on "is this a campaign signup" made the calls strictly sequential and
  // doubled how long we hold. In parallel they cost one round trip between them.
  //
  // Neither is an EXTRA request for anyone we let through: `UnpaidOrdersGate`
  // inside <Layout /> fetches `['unpaid-orders']` with this exact queryFn and
  // staleTime, and most member pages mount `useMySlots`, which is this exact
  // `['my-slots']` entry. This is those same requests, issued a round trip
  // earlier and shared from cache. A lock-OFF tenant issues neither.
  const slotsQ = useQuery({ ...mySlotsQuery, enabled: lockEnabled });
  const ordersQ = useQuery({ ...unpaidOrdersQuery, enabled: lockEnabled });

  return renderOrderGate(
    decideOrderGate({
      pathname: location.pathname,
      lockEnabled,
      // `isPending` is TRUE for a DISABLED query, which is why the lock-off case
      // must be decided before any of these is consulted — see decideOrderGate.
      mePending: meQ.isPending,
      registeredViaCampaign: meQ.data?.registeredViaCampaign,
      slotsPending: slotsQ.isPending,
      // Same definition `useMySlots` uses for `hasAnyActiveSlot`, so the guard
      // and the pages agree on what "activated" means.
      hasAnyActiveSlot: (slotsQ.data ?? []).some((sl) => sl.status === 'active'),
      ordersPending: ordersQ.isPending,
      orders: ordersQ.data,
    }),
  );
}

function renderOrderGate(d: OrderGateDecision) {
  if (d.kind === 'hold') return <BootSplash />;
  if (d.kind === 'redirect') return <Navigate to={`/pay/${d.orderId}`} replace />;
  return <Outlet />;
}
